import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { tagsAPI, drillsAPI, linesAPI } from '../services/api';
import FeedbackWidget from './FeedbackWidget';
import './DrillsSession.css';

const DEFAULT_TOTAL_QUESTIONS = 10;
const SUPPORTED_QUESTION_COUNTS = [5, 10];
const QUESTION_TIME_LIMIT_SECONDS = 180;
const QUESTION_BATCH_PLAN_BY_TOTAL = {
  5: [1, 2, 2],
  10: [1, 3, 4, 2],
};

const createEmptyDrillContext = () => ({
  place: '',
  resistanceLevel: '',
  stage: '',
});

const normalizeQuestionCount = (value) => {
  const parsedValue = Number.parseInt(value, 10);
  return SUPPORTED_QUESTION_COUNTS.includes(parsedValue) ? parsedValue : DEFAULT_TOTAL_QUESTIONS;
};

const formatMetricScore = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? `${numericValue.toFixed(1)}/10` : null;
};

const formatResponseTime = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? `${numericValue.toFixed(1)}s` : null;
};

const CREATIVITY_LABELS = {
  super_generic: 'Super Generic',
  generic: 'Generic',
  somewhat_creative: 'Somewhat Creative',
  creative: 'Creative',
};

const formatCreativityLabel = (value) => (
  CREATIVITY_LABELS[value] || String(value || '').replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())
);

const normalizePotentialAnswers = (potentialAnswers, potentialAnswerBreakdowns = null) => {
  const source = Array.isArray(potentialAnswerBreakdowns) && potentialAnswerBreakdowns.length > 0
    ? potentialAnswerBreakdowns
    : potentialAnswers;

  if (!Array.isArray(source)) return [];

  return source.flatMap((item) => {
    if (typeof item === 'string') return [{ answer: item, explanation: '' }];
    if (Array.isArray(item?.answers)) {
      return item.answers.map(answer => ({ answer, explanation: '' }));
    }
    const answer = item?.answer || item?.response || item?.text || '';
    if (answer) {
      return [{
        answer,
        explanation: item?.explanation || item?.breakdown || '',
      }];
    }
    return [];
  }).filter(item => item.answer);
};

const getCueFeedbackSections = (feedback) => {
  if (feedback && typeof feedback === 'object' && !Array.isArray(feedback)) {
    return [
      ['Technique Fit', feedback.technique_fit],
      ['Evaluation', feedback.creativity || feedback.evaluation],
      ['Your Response', feedback.response],
    ].filter(([, text]) => text);
  }

  return [];
};

const formatPracticeDate = (value) => {
  if (!value) return null;

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return null;

  return parsedDate.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const normalizeAssociatedLine = (line) => {
  const content = line?.content?.trim();
  if (!content) return null;

  const metrics = line?.performance_metrics || {};

  return {
    id: line?.id ?? line?.line_id ?? line?.pk ?? content,
    content,
    performanceScore: line?.performance_score ?? metrics.average_overall_score ?? null,
    attemptCount: metrics.attempt_count ?? 0,
    averageRecallScore: metrics.average_recall_score ?? null,
    averageCreativityScore: metrics.average_creativity_score ?? null,
    averageOverallScore: metrics.average_overall_score ?? line?.performance_score ?? null,
    averageResponseTimeSeconds: metrics.average_response_time_seconds ?? null,
    lastPracticed: metrics.last_practiced ?? line?.last_practiced ?? null,
  };
};

const getTagChipStyle = (index) => {
  const hue = Math.round((index * 137.5) % 360);

  return {
    backgroundColor: `hsl(${hue}, 42%, 18%)`,
    borderColor: `hsl(${hue}, 55%, 38%)`,
    color: 'var(--text-1)',
  };
};

const getQuestionBatchPlan = (questionCount) => {
  const configuredPlan = QUESTION_BATCH_PLAN_BY_TOTAL[questionCount];

  if (configuredPlan) {
    return configuredPlan;
  }

  return [1, Math.max(questionCount - 1, 0)].filter(batchSize => batchSize > 0);
};

const getQuestionNumber = (question, fallbackQuestionNumber) => {
  const numericQuestionNumber = Number(question?.question_number);

  return Number.isInteger(numericQuestionNumber) && numericQuestionNumber > 0
    ? numericQuestionNumber
    : fallbackQuestionNumber;
};

const mergeQuestionsByNumber = (existingQuestions, incomingQuestions) => {
  const questionsByNumber = new Map();

  existingQuestions.forEach((question, index) => {
    const questionNumber = getQuestionNumber(question, index + 1);
    questionsByNumber.set(questionNumber, {
      ...question,
      question_number: questionNumber,
    });
  });

  incomingQuestions.forEach((question, index) => {
    const fallbackQuestionNumber = questionsByNumber.size + index + 1;
    const questionNumber = getQuestionNumber(question, fallbackQuestionNumber);

    questionsByNumber.set(questionNumber, {
      ...question,
      question_number: questionNumber,
    });
  });

  return Array.from(questionsByNumber.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([, question]) => question);
};

const createSkippedEvaluation = ({ questionNumber, cue, skipReason, gameType }) => {
  const isTimedOut = skipReason === 'timeout';
  const baseEvaluation = {
    feedback: isTimedOut
      ? 'Time limit reached. This question was skipped and not scored.'
      : 'Question skipped. No answer was submitted for scoring.',
    cue,
    questionNumber,
    is_skipped_or_irrelevant_response: true,
  };

  if (gameType === 'cue_game') {
    return {
      ...baseEvaluation,
      meets_practiced_technique: false,
      creativity_label: 'super_generic',
      creativity_score: 0,
      overall_score: 0,
      feedback: {
        technique_fit: baseEvaluation.feedback,
        creativity: 'No score for skipped answers.',
        response: 'No answer was submitted.',
      },
      potential_answers: [],
    };
  }

  return {
    ...baseEvaluation,
    exact_response: '',
    alternative_responses: [],
    recall_score: 0,
    creativity_score: 0,
  };
};

const DrillsSession = ({ tagId, gameType = 'drill', locationState = null }) => {
  const navigate = useNavigate();
  const sessionStartedRef = useRef(false); // Prevent double API calls
  const recallPreviewInitializedRef = useRef(false); // Prevent duplicate preview API calls
  const prefetchBatchSizesRef = useRef([]);
  const prefetchInFlightRef = useRef(false);
  const totalQuestions = normalizeQuestionCount(locationState?.totalQuestions);
  const llmModel = locationState?.llmModel || '';
  
  const [tags, setTags] = useState([]);
  const [questions, setQuestions] = useState([]); // Array of question objects
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [currentResponse, setCurrentResponse] = useState('');
  const [evaluation, setEvaluation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showEvaluation, setShowEvaluation] = useState(false);
  const [timer, setTimer] = useState(0);
  const timerIntervalRef = useRef(null); // Use ref to persist interval across renders
  const timerStartTimeRef = useRef(null);
  const [tagInfo, setTagInfo] = useState(null);
  const [selectedTagIds, setSelectedTagIds] = useState([]); // Additional tags for cue game
  const [evaluatedCue, setEvaluatedCue] = useState(null);
  const [allResponses, setAllResponses] = useState([]); // Track all responses for analytics
  const drillContext = {
    ...createEmptyDrillContext(),
    ...(locationState?.drillContext || {})
  };
  const [fetchingQuestions, setFetchingQuestions] = useState(false); // Prevent duplicate fetches
  const [sessionEndedEarly, setSessionEndedEarly] = useState(false); // Track if user ended session early
  const [showSessionResults, setShowSessionResults] = useState(false);
  const [isQuestionTimedOut, setIsQuestionTimedOut] = useState(false);
  const [associatedLines, setAssociatedLines] = useState([]); // Recall game warm-up lines
  const [isLoadingAssociatedLines, setIsLoadingAssociatedLines] = useState(false);
  const [showRecallPreview, setShowRecallPreview] = useState(false);
  const currentQuestion = questions[currentQuestionIndex] || null;
  const currentCue = currentQuestion?.cue || null;
  const currentScenarioContext = currentQuestion?.metadata?.scenario_context || null;
  const currentQuestionNumber = getQuestionNumber(currentQuestion, currentQuestionIndex + 1);
  const isLastQuestion = currentQuestionIndex >= totalQuestions - 1;

  const resetQuestionState = () => {
    setShowEvaluation(false);
    setEvaluation(null);
    setEvaluatedCue(null);
    setCurrentResponse('');
    setIsQuestionTimedOut(false);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, []);

  // Load tags on mount
  useEffect(() => {
    loadTags();
  }, []);

  const getLineTagId = (line) => {
    if (line?.tag && typeof line.tag === 'object') {
      return line.tag.id ?? line.tag.tag_id ?? line.tag.pk ?? null;
    }

    if (typeof line?.tag !== 'undefined' && line?.tag !== null) {
      return line.tag;
    }

    return line?.tag_id ?? null;
  };

  const loadAssociatedLines = async (selectedTagId) => {
    try {
      setIsLoadingAssociatedLines(true);

      const tag = tags.find(t => t.id === selectedTagId);
      if (tag) {
        setTagInfo(tag);
      }

      let linesForTag = [];

      // Prefer tag detail endpoint (typically includes nested lines).
      try {
        const tagDetails = await tagsAPI.getById(selectedTagId);
        linesForTag = Array.isArray(tagDetails.lines) ? tagDetails.lines : [];
      } catch (error) {
        console.warn('Falling back to lines list API for recall preview lines:', error);
      }

      // Fallback in case tag details don't include lines.
      if (linesForTag.length === 0) {
        const linesResponse = await linesAPI.getAll({ tag_id: selectedTagId });
        const allLines = linesResponse.results || linesResponse || [];

        if (Array.isArray(allLines)) {
          linesForTag = allLines.filter(line => String(getLineTagId(line)) === String(selectedTagId));
        }
      }

      const normalizedLines = linesForTag
        .map(normalizeAssociatedLine)
        .filter(Boolean);

      if (normalizedLines.length === 0) {
        alert('This tag has no lines yet. Add lines before starting a recall drill.');
        navigate('/drills');
        return;
      }

      setAssociatedLines(normalizedLines);
      setShowRecallPreview(true);
    } catch (error) {
      console.error('Error loading associated lines for recall preview:', error);
      alert(error.error || error.message || 'Failed to load lines for recall warm-up. Please try again.');
      navigate('/drills');
    } finally {
      setIsLoadingAssociatedLines(false);
    }
  };

  // Start session when tagId is available (prevent double calls)
  useEffect(() => {
    if (!tagId || tags.length === 0) return;

    const parsedTagId = parseInt(tagId, 10);
    if (Number.isNaN(parsedTagId)) {
      alert('Invalid tag selected for drill session.');
      navigate('/drills');
      return;
    }

    const additionalTagIds = locationState?.selectedTagIds || [];
    setSelectedTagIds(additionalTagIds);

    if (gameType === 'cue_game') {
      if (sessionStartedRef.current) return;
      sessionStartedRef.current = true;
      startSession(parsedTagId, additionalTagIds);
      return;
    }

    if (recallPreviewInitializedRef.current) return;
    recallPreviewInitializedRef.current = true;
    loadAssociatedLines(parsedTagId);
  }, [tagId, tags, locationState, gameType]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTags = async () => {
    try {
      const response = await tagsAPI.getAll();
      setTags(response.results || []);
    } catch (error) {
      console.error('Error loading tags:', error);
    }
  };

  // Timer functions
  const startTimer = () => {
    // Clear any existing interval
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    
    timerStartTimeRef.current = Date.now();
    setIsQuestionTimedOut(false);
    setTimer(0);
    
    timerIntervalRef.current = setInterval(() => {
      if (timerStartTimeRef.current) {
        const elapsed = (Date.now() - timerStartTimeRef.current) / 1000;
        const cappedTime = Math.min(elapsed, QUESTION_TIME_LIMIT_SECONDS);
        const newTime = Math.round(cappedTime * 10) / 10;
        setTimer(newTime);

        if (elapsed >= QUESTION_TIME_LIMIT_SECONDS) {
          setIsQuestionTimedOut(true);
          stopTimer();
        }
      }
    }, 100);
  };

  const stopTimer = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    timerStartTimeRef.current = null;
  };

  const resetTimer = () => {
    stopTimer();
    setTimer(0);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = (seconds % 60).toFixed(1);
    return `${mins}:${secs.padStart(4, '0')}`;
  };

  const fetchMoreQuestions = async (activeSessionId) => {
    if (!activeSessionId || prefetchInFlightRef.current || prefetchBatchSizesRef.current.length === 0) {
      return;
    }

    try {
      prefetchInFlightRef.current = true;
      setFetchingQuestions(true);

      while (prefetchBatchSizesRef.current.length > 0) {
        const nextBatchSize = prefetchBatchSizesRef.current[0];
        const data = await drillsAPI.generateQuestions({
          sessionId: activeSessionId,
          numQuestions: nextBatchSize
        });
        const newQuestions = data.questions || [];

        if (newQuestions.length > 0) {
          setQuestions(prevQuestions => (
            mergeQuestionsByNumber(prevQuestions, newQuestions).slice(0, totalQuestions)
          ));
        }

        prefetchBatchSizesRef.current.shift();
      }
    } catch (error) {
      console.error('Error fetching more questions:', error);
    } finally {
      prefetchInFlightRef.current = false;
      setFetchingQuestions(false);
    }
  };

  const startSession = async (tagId, additionalTagIds = []) => {
    try {
      setIsLoading(true);
      setSessionEndedEarly(false);
      setShowSessionResults(false);
      resetQuestionState();
      setCurrentQuestionIndex(0);
      setQuestions([]);
      setAllResponses([]);
      setSessionId(null);
      prefetchBatchSizesRef.current = [];
      prefetchInFlightRef.current = false;

      const tag = tags.find(t => t.id === tagId);
      if (tag) {
        setTagInfo(tag);
      }

      const gameTypeParam = gameType === 'cue_game' ? 'cue_game' : 'drill';
      const sessionData = await drillsAPI.createSession({
        tagId,
        gameType: gameTypeParam,
        totalQuestions,
        place: drillContext.place,
        resistanceLevel: drillContext.resistanceLevel,
        stage: drillContext.stage,
        selectedTags: additionalTagIds,
        llmModel
      });
      const createdSessionId = sessionData?.session_id;

      if (!createdSessionId) {
        throw new Error('Session creation did not return a session_id.');
      }

      setSessionId(createdSessionId);

      // Step 1: Generate the first visible question.
      const data = await drillsAPI.generateQuestions({
        sessionId: createdSessionId,
        numQuestions: 1
      });
      const generatedQuestions = data.questions || [];
      
      if (generatedQuestions.length === 0) {
        throw new Error('No questions could be generated. Please try again.');
      }

      setQuestions(mergeQuestionsByNumber([], generatedQuestions).slice(0, totalQuestions));
      setCurrentQuestionIndex(0);

      // Step 2: Prefetch the remaining questions serially in the background.
      prefetchBatchSizesRef.current = getQuestionBatchPlan(totalQuestions).slice(1);
      void fetchMoreQuestions(createdSessionId);
      
      // Show first question and start timer
      setTimeout(() => {
        startTimer();
      }, 100);
    } catch (error) {
      console.error('Error starting session:', error);
      alert(error.error || error.message || 'Failed to start drill session. Please try again.');
      navigate('/drills');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartRecallQuiz = () => {
    if (!tagId || associatedLines.length === 0) {
      alert('No lines available for this tag yet.');
      navigate('/drills');
      return;
    }

    const parsedTagId = parseInt(tagId, 10);
    if (Number.isNaN(parsedTagId)) {
      alert('Invalid tag selected for drill session.');
      navigate('/drills');
      return;
    }

    if (sessionStartedRef.current) return;

    sessionStartedRef.current = true;
    setShowRecallPreview(false);
    startSession(parsedTagId);
  };

  const finalizeQuestion = ({ rawEvaluation, userResponse, isSkipped = false, skipReason = null }) => {
    if (!currentQuestion) {
      return;
    }

    const evaluationPayload = isSkipped
      ? {
          ...createSkippedEvaluation({
            questionNumber: currentQuestionNumber,
            cue: currentQuestion.cue,
            skipReason,
            gameType,
          }),
          ...(rawEvaluation || {}),
        }
      : rawEvaluation;

    const evaluationData = {
      ...evaluationPayload,
      cue: currentQuestion.cue,
      questionNumber: currentQuestionNumber,
      line_id: evaluationPayload?.line_id || currentQuestion.line_id || null,
      submitted_user_response: isSkipped
        ? (skipReason === 'timeout' ? 'Timed out' : 'Skipped')
        : userResponse
    };
    
    setEvaluation(evaluationData);
    setEvaluatedCue(currentQuestion.cue);
    
    const responseData = {
      questionNumber: currentQuestionNumber,
      cue: currentQuestion.cue,
      scenarioContext: currentQuestion.metadata?.scenario_context || null,
      userResponse: isSkipped
        ? (skipReason === 'timeout' ? 'Timed out' : 'Skipped')
        : userResponse,
      drillContext: { ...drillContext },
      evaluation: evaluationPayload
    };
    
    if (gameType === 'cue_game') {
      responseData.meetsPracticedTechnique = evaluationPayload.meets_practiced_technique;
      responseData.creativityLabel = evaluationPayload.creativity_label;
      responseData.creativityScore = evaluationPayload.creativity_score;
      responseData.overallScore = evaluationPayload.overall_score;
      responseData.potentialAnswers = normalizePotentialAnswers(
        evaluationPayload.potential_answers,
        evaluationPayload.potential_answer_breakdowns
      );
      responseData.isSkipped = isSkipped || evaluationPayload.is_skipped_or_irrelevant_response || false;
    } else {
      responseData.exactResponse = evaluationPayload.exact_response || '';
      responseData.alternativeResponses = evaluationPayload.alternative_responses || [];
      responseData.recallScore = evaluationPayload.recall_score;
      responseData.creativityScore = evaluationPayload.creativity_score;
      responseData.isSkipped = isSkipped || evaluationPayload.is_skipped_or_irrelevant_response || false;
    }
    
    setAllResponses(prev => [...prev, responseData]);
    setShowEvaluation(true);
    setCurrentResponse('');
  };

  const submitCurrentResponse = async ({ userResponse, isSkipped = false, skipReason = null }) => {
    if (showEvaluation || questions.length === 0) return;

    stopTimer();
    const responseTime = Math.min(timer, QUESTION_TIME_LIMIT_SECONDS);

    if (!currentQuestion || !sessionId) {
      alert('No question available. Please try again.');
      return;
    }

    try {
      setIsLoading(true);

      const data = await drillsAPI.evaluateDrill({
        sessionId,
        questionNumber: currentQuestionNumber,
        cue: currentQuestion.cue,
        userResponse,
        responseTimeSeconds: responseTime
      });

      finalizeQuestion({
        rawEvaluation: data.evaluation,
        userResponse,
        isSkipped,
        skipReason,
      });
    } catch (error) {
      if (isSkipped) {
        console.warn('Skip evaluation fell back to client-side handling:', error);
        finalizeQuestion({
          rawEvaluation: null,
          userResponse,
          isSkipped: true,
          skipReason,
        });
      } else {
        console.error('Error evaluating response:', error);
        alert(error.error || error.message || 'Failed to evaluate response. Please try again.');
        startTimer();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!currentResponse.trim() || showEvaluation || questions.length === 0 || isQuestionTimedOut) return;

    await submitCurrentResponse({
      userResponse: currentResponse,
    });
  };

  const handleSkip = async (skipReason = 'manual') => {
    if (showEvaluation || questions.length === 0) return;

    await submitCurrentResponse({
      userResponse: '',
      isSkipped: true,
      skipReason,
    });
  };

  const handleContinue = () => {
    if (showSessionResults || sessionEndedEarly || (showEvaluation && evaluation !== null && isLastQuestion)) {
      setShowSessionResults(true);
      return;
    }
    
    const nextIndex = currentQuestionIndex + 1;
    
    if (nextIndex < totalQuestions && nextIndex < questions.length) {
      setCurrentQuestionIndex(nextIndex);
      resetQuestionState();
      
      // Start timer for next question
      setTimeout(() => {
        startTimer();
      }, 100);
    } else if (nextIndex >= totalQuestions) {
      setShowSessionResults(true);
    } else {
      if (!fetchingQuestions && sessionId) {
        void fetchMoreQuestions(sessionId);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !showEvaluation && !isQuestionTimedOut) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Calculate session summary
  const calculateSessionSummary = () => {
    if (allResponses.length === 0) return null;
    
    if (gameType === 'cue_game') {
      const answered = allResponses.filter(r => !r.isSkipped);
      const skipped = allResponses.filter(r => r.isSkipped);
      const techniquePassed = answered.filter(r => r.meetsPracticedTechnique).length;
      
      const avgCreativity = answered.length > 0
        ? answered.reduce((sum, r) => sum + (r.creativityScore || 0), 0) / answered.length
        : 0;
      const avgOverall = answered.length > 0
        ? answered.reduce((sum, r) => sum + (r.overallScore || 0), 0) / answered.length
        : 0;
      
      return {
        questionsAnswered: answered.length,
        questionsSkipped: skipped.length,
        techniquePassed,
        averageCreativityScore: avgCreativity,
        average_score: avgOverall,
        total_questions: totalQuestions
      };
    } else {
      const answered = allResponses.filter(r => !r.isSkipped);
      const skipped = allResponses.filter(r => r.isSkipped);
      
      const avgRecall = answered.length > 0 
        ? answered.reduce((sum, r) => sum + (r.recallScore || 0), 0) / answered.length 
        : 0;
      const avgCreativity = answered.length > 0
        ? answered.reduce((sum, r) => sum + (r.creativityScore || 0), 0) / answered.length
        : 0;
      const avgOverall = (avgRecall + avgCreativity) / 2;
      
      return {
        questionsAnswered: answered.length,
        questionsSkipped: skipped.length,
        averageRecallScore: avgRecall,
        averageCreativityScore: avgCreativity,
        average_score: avgOverall,
        total_questions: totalQuestions
      };
    }
  };

  // Session is completed if we've answered all configured questions OR user ended early.
  const isSessionCompleted = (isLastQuestion && showEvaluation && evaluation !== null) || sessionEndedEarly;
  const sessionSummary = calculateSessionSummary();
  const isWaitingForNextQuestion = !isSessionCompleted
    && currentQuestionIndex + 1 < totalQuestions
    && currentQuestionIndex + 1 >= questions.length;

  // Handle ending session early
  const handleEndSession = () => {
    setSessionEndedEarly(true);
    setShowSessionResults(true);
    stopTimer();
  };

  // Loading state
  if (isLoading && questions.length === 0) {
    return (
      <div className="drill-session">
        <div className="drill-container">
          <div className="drill-header">
            <div className="header-left">
              <h3>Starting Drill Session...</h3>
            </div>
            <div className="session-info">
              <button 
                className="btn btn-secondary end-session-header-btn"
                onClick={() => navigate('/drills')}
              >
                End Session
              </button>
            </div>
          </div>
          <div className="loading-content">
            <div className="pulse-bubble"></div>
          </div>
        </div>
      </div>
    );
  }

  // Recall warm-up loading state (before questions start)
  if (gameType !== 'cue_game' && isLoadingAssociatedLines && questions.length === 0) {
    return (
      <div className="drill-session">
        <div className="drill-container">
          <div className="drill-header">
            <div className="header-left">
              <h3>Preparing Recall Warm-up...</h3>
            </div>
            <div className="session-info">
              <button
                className="btn btn-secondary end-session-header-btn"
                onClick={() => navigate('/drills')}
              >
                End Session
              </button>
            </div>
          </div>
          <div className="loading-content">
            <div className="pulse-bubble"></div>
          </div>
        </div>
      </div>
    );
  }

  // Tag selection screen (before session starts)
  if (questions.length === 0 && !tagId) {
    return (
      <div className="drill-session">
        <div className="drill-container">
          <div className="drill-header">
            <div className="header-left">
              <h3>Select a Tag</h3>
            </div>
            <div className="session-info">
              <button 
                className="btn btn-secondary end-session-header-btn"
                onClick={() => navigate('/drills')}
              >
                End Session
              </button>
            </div>
          </div>
          <div className="tag-selection-screen">
            <p>Please select a tag from the drill games page.</p>
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/drills')}
            >
              Go to Drill Games
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Recall warm-up screen (before the first question starts)
  if (gameType !== 'cue_game' && showRecallPreview && questions.length === 0) {
    return (
      <div className="drill-session">
        <div className="drill-container">
          <div className="drill-header">
            <div className="header-left">
              {tagInfo && <h3>{tagInfo.name} Recall Warm-up</h3>}
              {!tagInfo && <h3>Recall Warm-up</h3>}
            </div>
            <div className="session-info">
              <button
                className="btn btn-secondary end-session-header-btn"
                onClick={() => navigate('/drills')}
              >
                End Session
              </button>
            </div>
          </div>

          <div className="drill-content">
            <div className="recall-preview-screen">
              <p>
                Review these lines once, then start your {totalQuestions}-question quiz when you are ready.
              </p>
              <div className="recall-preview-lines">
                <table className="recall-preview-table">
                  <thead>
                    <tr>
                      <th scope="col">Line</th>
                      <th scope="col">Performance</th>
                      <th scope="col">Attempts</th>
                      <th scope="col">Recall</th>
                      <th scope="col">Creativity</th>
                      <th scope="col">Overall</th>
                      <th scope="col">Avg Response</th>
                      <th scope="col">Last Practiced</th>
                    </tr>
                  </thead>
                  <tbody>
                    {associatedLines.map((line, index) => (
                      <tr key={`${line.id}-${index}`} className="recall-preview-row">
                        <td className="recall-preview-line-cell">{line.content}</td>
                        <td>{formatMetricScore(line.performanceScore) || 'Not yet scored'}</td>
                        <td>{line.attemptCount > 0 ? line.attemptCount : 'No history'}</td>
                        <td>{line.attemptCount > 0 ? (formatMetricScore(line.averageRecallScore) || 'N/A') : 'N/A'}</td>
                        <td>{line.attemptCount > 0 ? (formatMetricScore(line.averageCreativityScore) || 'N/A') : 'N/A'}</td>
                        <td>{line.attemptCount > 0 ? (formatMetricScore(line.averageOverallScore) || 'N/A') : 'N/A'}</td>
                        <td>{line.attemptCount > 0 ? (formatResponseTime(line.averageResponseTimeSeconds) || 'N/A') : 'N/A'}</td>
                        <td>{line.attemptCount > 0 ? (formatPracticeDate(line.lastPracticed) || 'N/A') : 'No history'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="recall-preview-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => navigate('/drills')}
                >
                  Back to Drills
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleStartRecallQuiz}
                  disabled={associatedLines.length === 0}
                >
                  Start {totalQuestions}-Question Quiz
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Session completion screen - show only after the user explicitly leaves the last evaluation screen
  if (showSessionResults) {
    const summary = calculateSessionSummary();
    const completionAllTagIds = gameType === 'cue_game' && tagId
      ? [parseInt(tagId), ...selectedTagIds]
      : [];
    
    return (
      <div className="drill-session">
        <div className="drill-container">
          <div className="drill-header">
            <div className="header-left">
              {gameType === 'cue_game' ? (
                <>
                  <h3>Cue drills session</h3>
                  {completionAllTagIds.length > 0 && (
                    <div className="tags-chips" style={{ display: 'inline-flex', marginLeft: '12px', gap: '8px' }}>
                      {completionAllTagIds.map((id, index) => {
                        const tag = tags.find(t => t.id === id);
                        if (!tag) return null;
                        return (
                          <span key={id} className="tag-chip" style={getTagChipStyle(index)}>
                            {tag.name}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {tagInfo && <h3>{tagInfo.name} Drills session</h3>}
                  {!tagInfo && <h3>Drill Session Completed!</h3>}
                </>
              )}
            </div>
            <div className="session-info">
              <button 
                className="btn btn-secondary end-session-header-btn"
                onClick={() => navigate('/drills')}
              >
                Back to Drills
              </button>
            </div>
          </div>

          <div className="completion-screen">
            <div className="completion-icon">🎉</div>
            <h2>Session {sessionEndedEarly && allResponses.length > 0 ? 'Ended Early' : 'Complete'}!</h2>
            {allResponses.length === 0 && (
              <p>You ended the session early. No questions were answered yet.</p>
            )}
            {sessionEndedEarly && allResponses.length > 0 && (
              <p>
                You ended the session after answering {allResponses.length} of {totalQuestions} question{totalQuestions !== 1 ? 's' : ''}.
              </p>
            )}
            
            {summary && (
              <div className="session-analytics">
                <div className="analytics-grid">
                  <div className="analytics-stat">
                    <span className="stat-label">Questions Answered</span>
                    <span className="stat-value">{summary.questionsAnswered}</span>
                  </div>
                  {gameType === 'cue_game' ? (
                    <>
                      <div className="analytics-stat">
                        <span className="stat-label">Questions Skipped</span>
                        <span className="stat-value">{summary.questionsSkipped || 0}</span>
                      </div>
                      <div className="analytics-stat">
                        <span className="stat-label">Technique Passed</span>
                        <span className="stat-value">{summary.techniquePassed || 0}</span>
                      </div>
                      <div className="analytics-stat">
                        <span className="stat-label">Creativity Score</span>
                        <span className="stat-value">{summary.averageCreativityScore?.toFixed(1) || 'N/A'}</span>
                      </div>
                      <div className="analytics-stat">
                        <span className="stat-label">Average Final Score</span>
                        <span className="stat-value">{summary.average_score?.toFixed(1) || 'N/A'}</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="analytics-stat">
                        <span className="stat-label">Questions Skipped</span>
                        <span className="stat-value">{summary.questionsSkipped || 0}</span>
                      </div>
                      <div className="analytics-stat">
                        <span className="stat-label">Recall Score</span>
                        <span className="stat-value">{summary.averageRecallScore?.toFixed(1) || 'N/A'}</span>
                      </div>
                      <div className="analytics-stat">
                        <span className="stat-label">Creativity Score</span>
                        <span className="stat-value">{summary.averageCreativityScore?.toFixed(1) || 'N/A'}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {allResponses.length > 0 && (
              <div className="responses-table-container">
                <h3>All Responses</h3>
                <table className="responses-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Scenario & Cue</th>
                      <th>Your Response</th>
                      {gameType === 'cue_game' ? (
                        <>
                          <th>Potential Answers</th>
                          <th>Technique</th>
                          <th>Creativity</th>
                          <th>Final Score</th>
                        </>
                      ) : (
                        <>
                          <th>Exact Response</th>
                          <th>Alternative Responses</th>
                          <th>Status</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {allResponses.map((response, index) => (
                      <tr key={index} className={response.isSkipped ? 'skipped-row' : ''}>
                        <td className="question-num-cell">{response.questionNumber}</td>
                        <td className="cue-cell">
                          {response.scenarioContext && (
                            <div style={{ marginBottom: '8px', fontStyle: 'italic', color: 'var(--text-2)', fontSize: '14px' }}>
                              <ReactMarkdown>{response.scenarioContext}</ReactMarkdown>
                            </div>
                          )}
                          <ReactMarkdown>{response.cue}</ReactMarkdown>
                        </td>
                        <td className="user-response-cell">{response.userResponse}</td>
                        {gameType === 'cue_game' ? (
                          <>
                            <td className="alternatives-cell">
                              {response.potentialAnswers && response.potentialAnswers.length > 0 ? (
                                <ul className="alternatives-inline-list">
                                  {response.potentialAnswers.map((answer, answerIndex) => (
                                    <li key={answerIndex}>
                                      <span>{answer.answer}</span>
                                      {answer.explanation && (
                                        <span className="inline-answer-explanation"> {answer.explanation}</span>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                'N/A'
                              )}
                            </td>
                            <td className="score-cell">{response.meetsPracticedTechnique ? 'Yes' : 'No'}</td>
                            <td className="score-cell">
                              <span>{response.creativityLabel ? formatCreativityLabel(response.creativityLabel) : 'N/A'}</span>
                              {Number.isFinite(Number(response.creativityScore)) && (
                                <span> ({Number(response.creativityScore).toFixed(1)})</span>
                              )}
                            </td>
                            <td className="score-cell">
                              {Number.isFinite(Number(response.overallScore ?? response.evaluation?.overall_score))
                                ? Number(response.overallScore ?? response.evaluation?.overall_score).toFixed(1)
                                : 'N/A'}
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="exact-response-cell">{response.exactResponse || 'N/A'}</td>
                            <td className="alternatives-cell">
                              {response.alternativeResponses && response.alternativeResponses.length > 0 ? (
                                <ul className="alternatives-inline-list">
                                  {response.alternativeResponses.map((alt, altIndex) => (
                                    <li key={altIndex}>{alt}</li>
                                  ))}
                                </ul>
                              ) : (
                                'N/A'
                              )}
                            </td>
                            <td className="status-cell">
                              {response.isSkipped ? (
                                <span className="status-badge skipped">Skipped</span>
                              ) : (
                                <span className="status-badge answered">
                                  Recall: {response.recallScore?.toFixed(1) || 'N/A'} | 
                                  Creativity: {response.creativityScore?.toFixed(1) || 'N/A'}
                                </span>
                              )}
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <button 
              className="btn btn-primary end-session-btn"
              onClick={() => navigate('/drills')}
            >
              End Session
            </button>
          </div>
        </div>
      </div>
    );
  }


  // Get all tag IDs (primary + selected) for cue game
  const allTagIds = gameType === 'cue_game' && tagId
    ? [parseInt(tagId), ...selectedTagIds]
    : [];

  return (
    <div className="drill-session">
      <div className="drill-container">
        <div className="drill-header">
          <div className="header-left">
            {gameType === 'cue_game' ? (
              <>
                <h3>Cue drills session</h3>
                {allTagIds.length > 0 && (
                  <div className="tags-chips" style={{ display: 'inline-flex', marginLeft: '12px', gap: '8px' }}>
                    {allTagIds.map((id, index) => {
                      const tag = tags.find(t => t.id === id);
                      if (!tag) return null;
                      return (
                        <span key={id} className="tag-chip" style={getTagChipStyle(index)}>
                          {tag.name}
                        </span>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <>
                {tagInfo && <h3>{tagInfo.name} Drills session</h3>}
                {!tagInfo && <h3>Drills session</h3>}
              </>
            )}
          </div>
            <div className="session-info">
              <span>Question: {currentQuestionNumber}/{totalQuestions}</span>
              {!showEvaluation && (
                <button 
                  className="btn btn-secondary end-session-header-btn"
                  onClick={handleEndSession}
                >
                  End Session
                </button>
              )}
            </div>
        </div>

        <div className="drill-content">
          {showEvaluation && evaluation ? (
            // Evaluation Panel
            <div className="evaluation-panel">
              <h3>Question {evaluation.questionNumber || currentQuestionNumber} Results</h3>
              
              {evaluatedCue && (
                <div className="evaluation-original-cue">
                  <h4>Original Cue:</h4>
                  <div className="cue-text">
                    <ReactMarkdown>{evaluatedCue}</ReactMarkdown>
                  </div>
                </div>
              )}

              {evaluation.line_id && (() => {
                const evaluatedLine = associatedLines.find(l => l.id === evaluation.line_id);
                return evaluatedLine ? (
                  <div className="evaluation-original-line">
                    <h4>Original Line:</h4>
                    <div className="original-line-content">{evaluatedLine.content}</div>
                  </div>
                ) : null;
              })()}

              {evaluation.submitted_user_response && (
                <div className="evaluation-exact-response">
                  <h4>Your Response:</h4>
                  <p className="response-text">{evaluation.submitted_user_response}</p>
                </div>
              )}

              {gameType !== 'cue_game' && (
                <div className="evaluation-feedback">
                  <h4>Feedback:</h4>
                  <ReactMarkdown>{evaluation.feedback}</ReactMarkdown>
                </div>
              )}

              {evaluation.is_skipped_or_irrelevant_response && (
                <div className="evaluation-skip-notice">
                  This question was skipped, so no answer was scored.
                </div>
              )}
              
              {gameType === 'cue_game' ? (
                // Cue game evaluation structure
                <>
                  {getCueFeedbackSections(evaluation.feedback).length > 0 ? (
                    <div className="evaluation-feedback">
                      <h4>Feedback:</h4>
                      {getCueFeedbackSections(evaluation.feedback).map(([label, text]) => (
                        <div key={label} className="feedback-section">
                          <h5>{label}</h5>
                          <ReactMarkdown>{text}</ReactMarkdown>
                        </div>
                      ))}
                    </div>
                  ) : (
                    typeof evaluation.feedback === 'string' && evaluation.feedback && (
                      <div className="evaluation-feedback">
                        <h4>Feedback:</h4>
                        <ReactMarkdown>{evaluation.feedback}</ReactMarkdown>
                      </div>
                    )
                  )}
                  
                  {normalizePotentialAnswers(
                    evaluation.potential_answers,
                    evaluation.potential_answer_breakdowns
                  ).length > 0 && (
                    <div className="evaluation-potential-answers">
                      <h4>Potential Answers:</h4>
                      <ul className="potential-answers-list">
                        {normalizePotentialAnswers(
                          evaluation.potential_answers,
                          evaluation.potential_answer_breakdowns
                        ).map((item, answerIndex) => (
                          <li key={answerIndex} className="potential-answer-item">
                            <ReactMarkdown>{item.answer}</ReactMarkdown>
                            {item.explanation && (
                              <div className="potential-answer-explanation">
                                <ReactMarkdown>{item.explanation}</ReactMarkdown>
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {!evaluation.is_skipped_or_irrelevant_response && (
                    <div className="evaluation-scores">
                      <div className="score-item">
                        <span className="score-label">Meets Technique</span>
                        <span className="score-value">{evaluation.meets_practiced_technique ? 'Yes' : 'No'}</span>
                      </div>
                      <div className="score-item">
                        <span className="score-label">Final Score</span>
                        <span className="score-value">
                          {Number.isFinite(Number(evaluation.overall_score)) ? Number(evaluation.overall_score).toFixed(1) : 'N/A'}
                        </span>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                // Drill game evaluation structure
                <>
                  {evaluation.alternative_responses && evaluation.alternative_responses.length > 0 && (
                    <div className="evaluation-alternatives">
                      <h4>Alternative Responses:</h4>
                      <ul className="alternative-list">
                        {evaluation.alternative_responses.map((alt, index) => (
                          <li key={index} className="alternative-item">{alt}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {!evaluation.is_skipped_or_irrelevant_response && (
                    <div className="evaluation-scores">
                      <div className="score-item">
                        <span className="score-label">Recall</span>
                        <span className="score-value">{evaluation.recall_score?.toFixed(1)}</span>
                      </div>
                      <div className="score-item">
                        <span className="score-label">Creativity</span>
                        <span className="score-value">{evaluation.creativity_score?.toFixed(1)}</span>
                      </div>
                      <div className="score-item">
                        <span className="score-label">Overall</span>
                        <span className="score-value">{((evaluation.recall_score + evaluation.creativity_score) / 2)?.toFixed(1)}</span>
                      </div>
                    </div>
                  )}
                </>
              )}
              
            </div>
          ) : (
            // Question Panel
            <div className="question-panel">
              <div className="question-header">
                <span className="question-number">Question {currentQuestionNumber} of {totalQuestions}</span>
              </div>
              <div className="question-cue">
                {currentScenarioContext && (
                  <div className="cue-section">
                    <div className="cue-label">Context:</div>
                    <div className="cue-content">
                      <ReactMarkdown>{currentScenarioContext}</ReactMarkdown>
                    </div>
                  </div>
                )}
                <div className="cue-section">
                  <div className="cue-label">Cue:</div>
                  <div className="cue-content">
                    <ReactMarkdown>{currentCue}</ReactMarkdown>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="drill-input">
          <div className="input-actions-row">
            {showEvaluation ? (
              <>
                <button 
                  className="btn btn-primary continue-btn"
                  onClick={handleContinue}
                  disabled={isLoading || (isWaitingForNextQuestion && fetchingQuestions)}
                >
                  {isLastQuestion
                    ? 'View Final Results'
                    : (isWaitingForNextQuestion && fetchingQuestions)
                      ? 'Loading next question...'
                      : 'Continue'}
                </button>
                
                {/* Evaluation Feedback */}
                <FeedbackWidget
                  onSubmit={async (isPositive, textFeedback) => {
                    if (evaluation && evaluatedCue) {
                      // Find the user response from allResponses for this question
                      const responseData = allResponses.find(
                        r => r.questionNumber === (evaluation.questionNumber || currentQuestionNumber)
                      );
                      const userResponse = responseData?.userResponse || '';
                      
                      await drillsAPI.submitEvaluationFeedback(
                        null, // No drill_response_id in this flow
                        isPositive,
                        textFeedback,
                        evaluatedCue,
                        userResponse,
                        evaluation.line_id || null // May be null for cue game
                      );
                    }
                  }}
                  disabled={isLoading}
                />
              </>
            ) : (
              <>
                <textarea
                  value={currentResponse}
                  onChange={(e) => setCurrentResponse(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={isQuestionTimedOut ? 'Time limit reached. Skip to continue.' : 'Type your response here...'}
                  rows={4}
                  disabled={isLoading || showEvaluation || isQuestionTimedOut}
                />
                {isQuestionTimedOut && (
                  <div className="question-timeout-notice">
                    Time limit reached. This question can no longer be answered. Skip it to continue.
                  </div>
                )}
                <div className="submit-feedback-row">
                  <div className="question-action-buttons">
                    <button
                      className="btn btn-secondary skip-btn"
                      onClick={() => handleSkip(isQuestionTimedOut ? 'timeout' : 'manual')}
                      disabled={isLoading || showEvaluation}
                    >
                      {isQuestionTimedOut ? 'Skip And Continue' : 'Skip Question'}
                    </button>
                    <button 
                      className="btn btn-primary submit-btn"
                      onClick={handleSubmit}
                      disabled={!currentResponse.trim() || isLoading || showEvaluation || isQuestionTimedOut}
                    >
                      {isLoading ? 'Submitting...' : 'Submit'}
                    </button>
                  </div>

                  <div className="post-submit-controls">
                    <div className={`timer-display timer-compact ${isQuestionTimedOut ? 'timer-expired' : ''}`}>
                      <span className="timer-label">Time</span>
                      <span className="timer-value">{formatTime(timer)}</span>
                      {isQuestionTimedOut && <span className="timer-status">Limit reached</span>}
                    </div>
                  
                    {/* Question Feedback */}
                    <FeedbackWidget
                      onSubmit={async (isPositive, textFeedback) => {
                        if (currentCue) {
                          await drillsAPI.submitQuestionFeedback(
                            null, // No drill_response_id - questions don't create DrillResponse until user submits answer
                            currentCue,
                            isPositive,
                            textFeedback,
                            null, // line_id may be null for cue game
                            tagInfo?.id // Track which tag this question belongs to
                          );
                        }
                      }}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DrillsSession;
